import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import styles from './Apps.module.css';
import { useGetProfilesQuery } from 'services/data/profileApi';
import { DataGrid, useGridApiRef, gridClasses, GridRowModes, GridActionsCellItem, useGridApiContext } from '@mui/x-data-grid';
import { staticUrl } from 'services/data/mainUrl';
import { Close, Edit, Tune } from '@mui/icons-material';
import { editedToData } from 'utils/datagrid';
import { useDispatch, useSelector } from 'react-redux';
import { setAppsModesModel, editAppsModesModel, setEditedApps } from 'store/profile/profileSlice';
import { IconButton, InputAdornment, OutlinedInput, Select } from '@mui/material';
import MenuItem from "@mui/material/MenuItem";
import { useGetAppVersionsQuery } from 'services/data/appApi';
import StrippedDataGrid from 'components/styled';
import CloseIcon from '@mui/icons-material/Close';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';

const CustomEditComponent = ({id, value, field, row}) => {
  
  const [isOpen, setIsOpen] = useState(false);
  const apiRef = useGridApiContext();
  const {data: verData, error: verError} = useGetAppVersionsQuery({packageName: id }, {skip: !isOpen});
  const {data: modesData, isError: isModesError, error: modesError} = useGetProfilesQuery({mode: 'appModes'}, {skip: !isOpen});

  const handleChange = (event) => {  
    apiRef.current.setEditCellValue({
      id,
      field,
      value: event.target.value
    });
  };
 
  return (
    <Select
      labelId="profile-apps-version-label"
      id="profile-apps-version"
      value={value}
      onChange={handleChange}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      sx={{ width: "100%" }}      
    >
      {
        getAppVersion(row, verData, modesData?.body?.extraVersions).map((item) => {
          return <MenuItem key={item} value={item}>
            {item}
          </MenuItem> 
        })
      }
    </Select>
  );
}
const EnabledEcrsEditCell = (params) => <CustomEditComponent {...params} />;

const rowClassName = (row, indexRelativeToCurrentPage) => {
  let result = indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
  row.disabled && (result += ' disabled');
  return result;
};

const appNameRenderer = ({id, value}) => {
  return ( <div className={styles.appName}>
            <div className={styles.title}>{value.name}</div>
            <div className={styles.package}>{id}</div>
          </div>
  );
};

const getAppVersion = (row, data, extraData) => {
  if(data?.body?.items?.length > 0 && row.id === data.body.package) {
    return [ ...extraData, ...(data.body.items.map(item => item.version))];
  }
  return [row.version];
};

const Apps = forwardRef(({items, flush, flushData, modesData}, ref) => {
  
  //const {data: modesData, isError: isModesError, error: modesError} = useGetProfilesQuery({mode: 'appModes'});
  
  
  const dispatch = useDispatch();
  const { appsModesModel } = useSelector(state => state.profile);
  const apiRef = useGridApiRef();

  useEffect( () => {
    if(flush) { 
      flushData('apps', editedToData(apiRef.current.state.editRows).map(({id, ...others}) => ({package: id, ...others})));
    }
  }, [flush]);

  useLayoutEffect( () => {
    return () => { 
      dispatch(setEditedApps(editedToData(apiRef.current.state.editRows)));
    }
  }, []);
 
  const editClick = useCallback((id, row) => () => {
    dispatch(editAppsModesModel({[id]: {mode: GridRowModes.Edit}}));
  }, []);

  const cancelClick = useCallback((id) => () => {
    dispatch(editAppsModesModel({[id]: {mode: GridRowModes.View, ignoreModifications: true}}));
  }, []);

  const columns = useMemo(
    () => [
      {field: 'icon', width: 50, display: 'flex', align: 'center', sortable: false, 
        renderCell: (params) => <img className={styles.icon} src={`${staticUrl}${params.row.icon}`} />},
      {field: 'app', flex: 1, valueGetter: (value, row) => ({name: row.name, package: row.package}), renderCell: appNameRenderer, display: 'flex'},
      {field: 'version', type: 'singleSelect', editable: true, width: 170, renderEditCell: EnabledEcrsEditCell},
      {field: 'mode', type: 'singleSelect', width: 170, editable: true, 
        getOptionValue: (record) => record.id, getOptionLabel: (record) => record.value, valueOptions: (modesData?.body?.items.filter(el => el?.appVisible === true) ?? []) },
      {field: 'actions', headerName: '', type: 'actions', align: 'center', width: 50, 
        getActions: ({id, row}) => {
          const isEditMode = appsModesModel[id]?.mode === GridRowModes.Edit;
          if(isEditMode) return [
            <GridActionsCellItem
              icon={<Close sx={{color: 'rgba(0,0,0, 0.54)'}}/>}
              label="Отменить"
              onClick={cancelClick(id)}
              color="inherit"
            />
          ];
          return [
            <GridActionsCellItem
              icon={<Edit />}
              label="Изменить"
              onClick={editClick(id, row)}
            />
          ];
        }
      }  
    ]
  );
  return (
    <div ref={ref}>
      <StrippedDataGrid
        apiRef={apiRef}
        getRowClassName={({row, indexRelativeToCurrentPage}) => rowClassName(row, indexRelativeToCurrentPage)}
        editMode="row"
        className={styles.apps}
        rows={items}
        localeText={{noRowsLabel: "Приложений пока нет"}}
        columns={columns}
        rowModesModel={appsModesModel}
        experimentalFeatures={{ newEditingApi: true }}
        columnHeaderHeight={1}
        disableColumnResize
        disableColumnMenu
        hideFooter
        sx={{
          [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
            outline: 'none',
          },
          [`& .${gridClasses.columnHeader}, & .MuiInputBase-root`]: {
            overflowX: 'unset'
          },
          [`& .${gridClasses.cell}.${gridClasses["cell--editing"]}:focus, & .${gridClasses.cell}.${gridClasses["cell--editing"]}:focus-within`]: {
            outline: 'none'
          }
        }}
      />
      
    </div>
  );    
});

export default Apps;